/**
 * Benchmark Intelligence — Stage 18.
 * Privacy-safe: own history first, peer only when min-cohort satisfied.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, ShieldCheck, Info, ToggleLeft, ToggleRight } from "lucide-react";

export default function Benchmarks() {
  const [metrics, setMetrics] = useState([]);
  const [metric, setMetric] = useState("deployment_tat");
  const [own, setOwn] = useState(null);
  const [peer, setPeer] = useState(null);
  const [byCountry, setByCountry] = useState(null);
  const [participation, setParticipation] = useState(null);

  useEffect(() => { api.get("/benchmarks/metrics").then(({ data }) => setMetrics(data.items || [])); }, []);
  useEffect(() => {
    if (!metric) return;
    api.get("/benchmarks/own", { params: { metric } }).then(({ data }) => setOwn(data)).catch(() => setOwn(null));
    api.get("/benchmarks/peer", { params: { metric } }).then(({ data }) => setPeer(data)).catch(() => setPeer(null));
    api.get("/benchmarks/own/by-country", { params: { metric } }).then(({ data }) => setByCountry(data)).catch(() => setByCountry(null));
    api.get("/benchmarks/participation").then(({ data }) => setParticipation(data)).catch(() => setParticipation(null));
  }, [metric]);

  const toggleParticipation = async () => {
    if (!participation?.master_company_id) return toast.error("No tenant scope");
    const next = !participation.contribute;
    await api.post("/benchmarks/participation", {
      master_company_id: participation.master_company_id,
      contribute: next,
      basis: "customer_admin_consent",
    });
    setParticipation({ ...participation, contribute: next });
    toast.success(next ? "Contributing to anonymised benchmarks" : "Withdrawn from peer benchmark");
  };

  const m = metrics.find((x) => x.key === metric);

  return (
    <div className="max-w-5xl space-y-6" data-testid="benchmarks-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Insights</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Benchmark Intelligence</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Compare against your own history first. Peer benchmarks are aggregated, opt-in and suppressed when the cohort is too small to be privacy-safe.
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Metric</div>
          <select className="input min-w-[240px]" value={metric} onChange={(e) => setMetric(e.target.value)} data-testid="metric-select">
            {metrics.map((mm) => <option key={mm.key} value={mm.key}>{mm.name}</option>)}
          </select>
        </label>
        {participation && (
          <button onClick={toggleParticipation}
            className="ml-auto h-9 px-3 rounded-md border border-slate-200 bg-white text-sm inline-flex items-center gap-2"
            data-testid="participation-toggle">
            {participation.contribute ? <ToggleRight className="h-5 w-5 text-emerald-600" /> : <ToggleLeft className="h-5 w-5 text-slate-500" />}
            {participation.contribute ? "Contributing to peer benchmark" : "Own history only"}
          </button>
        )}
      </div>

      {m && (
        <div className="bg-white border border-slate-200 rounded-md p-4 text-xs text-slate-600 flex items-start gap-2">
          <Info className="h-4 w-4 text-slate-500 mt-0.5" />
          <div>
            <div className="font-semibold text-slate-900">{m.name}</div>
            <div>{m.definition}</div>
            <div className="text-slate-400 mt-1">Unit: {m.unit} · {m.lower_is_better ? "Lower is better" : "Higher is better"}</div>
          </div>
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ValueTile label="This month"  value={own?.this_month} unit={m?.unit} />
        <ValueTile label="Last quarter" value={own?.last_quarter} unit={m?.unit} />
        <ValueTile label="Last year"    value={own?.last_year} unit={m?.unit} />
      </section>

      {own?.improvement_pct != null && (
        <div className={"inline-flex items-center gap-1 text-sm font-medium rounded-md px-2 py-1 " + (own.improvement_pct >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700")}>
          {own.improvement_pct >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          {own.improvement_pct >= 0 ? "Improved" : "Regressed"} {Math.abs(own.improvement_pct)}% vs last year
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-md p-4" data-testid="peer-panel">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-slate-500" />
          <h2 className="text-lg font-heading font-semibold text-slate-900">Similar managed environments</h2>
        </div>
        {peer?.available ? (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <ValueTile label="Your median"  value={peer.your_median} unit={m?.unit} highlight />
            <ValueTile label="Peer median"  value={peer.peer_median} unit={m?.unit} />
            <ValueTile label="Peer p25"     value={peer.peer_p25} unit={m?.unit} />
            <ValueTile label="Peer p75"     value={peer.peer_p75} unit={m?.unit} />
            <div className="md:col-span-4 text-xs text-slate-500 mt-2">
              Position: <span className="text-slate-800 font-medium">{peer.position?.replace("_", " ") || "—"}</span> · cohort {peer.cohort_size_range} · period {peer.period_days} days.
              <br/>{peer.methodology}
            </div>
          </div>
        ) : (
          <div className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
            {peer?.message || "Peer benchmark not available yet."}
            {peer?.min_required && <div className="text-xs text-slate-500 mt-1">Minimum cohort required: {peer.min_required}.</div>}
          </div>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-md p-4">
        <h2 className="text-lg font-heading font-semibold text-slate-900">Your countries</h2>
        {byCountry?.countries?.length ? (
          <table className="w-full text-sm mt-2">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500">
              <tr><th className="text-left py-1">Country</th><th className="text-right py-1">Median</th><th className="text-right py-1">n</th></tr>
            </thead>
            <tbody>
              {byCountry.countries.map((c) => (
                <tr key={c.country} className="border-t border-slate-100">
                  <td className="py-2 text-slate-800">{c.country}</td>
                  <td className="py-2 text-right tabular-nums text-slate-900">{c.median ?? "—"} {m?.unit}</td>
                  <td className="py-2 text-right text-slate-500 tabular-nums">{c.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="text-sm text-slate-500 mt-2">No country data yet.</div>}
      </section>
    </div>
  );
}

function ValueTile({ label, value, unit, highlight }) {
  return (
    <div className={"rounded-md border p-3 " + (highlight ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200")}>
      <div className={"text-[10px] font-semibold uppercase tracking-widest " + (highlight ? "text-white/70" : "text-slate-500")}>{label}</div>
      <div className={"mt-1 text-2xl font-heading font-semibold tabular-nums " + (highlight ? "text-white" : "text-slate-900")}>
        {value ?? "—"} {value != null && unit ? <span className="text-sm font-normal opacity-70">{unit}</span> : null}
      </div>
    </div>
  );
}
